<template>
  <div
    class="modal-body call-panel-body"
    style="flex: 1; overflow: hidden; display: flex; flex-direction: column"
  >
    <div class="linkedin-layout">
      <div class="linkedin-subtabs">
        <button
          :class="['subtab', activeLinkedInTab === 'messages' ? 'active' : 'ghost']"
          @click.stop="handleLinkedInTabClick('messages')"
        >
          Messages
        </button>
        <button
          :class="['subtab', activeLinkedInTab === 'profile' ? 'active' : 'ghost']"
          @click.stop="handleLinkedInTabClick('profile')"
        >
          Profile
        </button>
        <button
          :class="['subtab', activeLinkedInTab === 'activities' ? 'active' : 'ghost']"
          @click.stop="handleLinkedInTabClick('activities')"
        >
          Activities
        </button>
        <div v-if="connectionStatusBadge === 'loading'" class="connection-status-loading">
          <LoadingBars size="sm" :show-text="false" />
        </div>
        <div
          v-else-if="connectionStatusBadge === 'connected'"
          class="connection-status-connected"
          title="You're connected on LinkedIn"
        >
          Connected
        </div>
        <div
          v-else-if="connectionStatusBadge === 'pending'"
          class="connection-status-pending"
          title="Connection request pending"
        >
          <Icon icon="mdi:clock-outline" class="connection-status-pending__icon" />
          <span>Pending</span>
        </div>
        <button
          v-else-if="connectionStatusBadge === 'connect'"
          type="button"
          class="pill-btn primary"
          :disabled="!canSendLinkedInInvite || isSendingLinkedInInvite"
          @click="handleConnectionRequest"
          style="margin-left: auto"
        >
          <LoadingBars v-if="isSendingLinkedInInvite" size="sm" :show-text="false" />
          <template v-else>
            <Icon icon="mdi:account-plus-outline" />
            <span>Connect</span>
          </template>
        </button>
      </div>

      <div class="linkedin-content">
        <div v-if="activeLinkedInTab === 'messages'" class="linkedin-messages-layout">
          <template v-if="isChannelsLoading">
            <div class="linkedin-loading-state">
              <LoadingBars size="sm" />
            </div>
          </template>
          <template v-else-if="!linkedInChannel?.isConnected || isLinkedInChannelDisconnected">
            <div class="linkedin-disconnected-state">
              <div class="linkedin-disconnected-content">
                <Icon icon="mdi:link-variant-off" class="linkedin-disconnected-icon" />
                <h3 class="linkedin-disconnected-title">Not Connected</h3>
                <p class="linkedin-disconnected-message">
                  Please connect your LinkedIn account to view conversations.
                </p>
                <button class="linkedin-disconnected-action" @click="$emit('reconnect-requested')">
                  <Icon icon="mdi:link" />
                  Connect Account
                </button>
              </div>
            </div>
          </template>
          <div
            v-else-if="!selectedBucket?.contact?.linkedInUrl"
            class="linkedin-empty-state linkedin-empty-state--missing-url"
          >
            <div class="linkedin-empty-state__content">
              <Icon icon="mdi:linkedin" class="linkedin-empty-state__icon" />
              <h3 class="linkedin-empty-state__title">LinkedIn URL Required</h3>
              <p class="linkedin-empty-state__message">
                Add a LinkedIn URL to this contact to view LinkedIn chats or send an invite.
              </p>
              <Button
                variant="primary"
                size="sm"
                icon="mdi:pencil-outline"
                @click="$emit('show-edit-contact-modal')"
              >
                Edit Contact
              </Button>
            </div>
          </div>
          <div v-else-if="chatAccessError" class="linkedin-chat-error-view">
            <!-- Tabs -->
            <div class="linkedin-chat-tabs">
              <button
                :class="['chat-tab', activeChatType !== 'inmail' ? 'active' : '']"
                @click="activeChatType = 'classic'"
              >
                <Icon icon="mdi:message-text-outline" class="chat-tab__icon" />
                Messages
              </button>
              <button
                :class="['chat-tab', activeChatType === 'inmail' ? 'active' : '']"
                @click="activeChatType = 'inmail'"
              >
                <Icon icon="mdi:email-fast-outline" class="chat-tab__icon" />
                InMail
              </button>
            </div>

            <!-- LinkedIn tab: Profile Restricted -->
            <div v-if="activeChatType !== 'inmail'" class="linkedin-restricted-state">
              <div class="linkedin-empty-state-icon">
                <Icon icon="mdi:lock-outline" />
              </div>
              <h4 class="linkedin-empty-state-heading">Profile Restricted</h4>
              <p class="linkedin-empty-state-text">
                This contact's LinkedIn profile is restricted or not accessible. Send them an InMail
                to reach out directly.
              </p>
            </div>

            <!-- InMail tab: full InMail thread -->
            <div v-else class="linkedin-inmail-thread">
              <div class="linkedin-inmail-thread__header">
                <div class="linkedin-inmail-thread__header-left">
                  <Icon icon="skill-icons:linkedin" class="linkedin-inmail-thread__icon" />
                  <div class="linkedin-inmail-thread__header-info">
                    <span class="linkedin-inmail-thread__title">InMail</span>
                    <span
                      :class="[
                        'linkedin-inmail-thread__status',
                        isLinkedInConnected
                          ? 'linkedin-inmail-thread__status--connected'
                          : 'linkedin-inmail-thread__status--disconnected',
                      ]"
                    >
                      {{ isLinkedInConnected ? "Connected" : "Not Connected" }}
                    </span>
                  </div>
                </div>
                <div
                  v-if="isLoadingInmailBalance"
                  class="linkedin-inmail-thread__credits-skeleton"
                />
                <div
                  v-else-if="inmailBalance !== null"
                  :class="[
                    'linkedin-inmail-thread__credits',
                    inmailCreditsCount === 0 ? 'linkedin-inmail-thread__credits--empty' : '',
                  ]"
                >
                  <span class="credits-value">{{ inmailCreditsCount }}</span>
                  <span class="credits-label">credits</span>
                </div>
              </div>

              <div class="linkedin-inmail-thread__messages" ref="inmailMessagesContainer">
                <div
                  v-if="isLoadingInmailBalance && sortedPendingInMails.length === 0"
                  class="linkedin-inmail-thread__loading"
                >
                  <LoadingBars size="sm" />
                </div>
                <template v-else>
                  <div
                    v-if="sortedPendingInMails.length === 0"
                    class="linkedin-inmail-thread__empty"
                  >
                    <div
                      class="linkedin-inmail-thread__empty-icon-wrap linkedin-inmail-thread__empty-icon-wrap--warning"
                    >
                      <Icon icon="mdi:lock-outline" class="linkedin-inmail-thread__empty-icon" />
                    </div>
                    <h4 class="linkedin-inmail-thread__empty-heading">Profile Restricted</h4>
                    <p class="linkedin-inmail-thread__empty-text">
                      This contact's profile is restricted or not accessible. Send them an InMail to
                      start a conversation.
                    </p>
                  </div>

                  <div
                    v-for="inmail in sortedPendingInMails"
                    :key="inmail.id"
                    class="linkedin-inmail-thread__message linkedin-inmail-thread__message--sent"
                  >
                    <div class="linkedin-inmail-thread__message-content">
                      <div class="linkedin-inmail-thread__message-subject">
                        {{ inmail.subject }}
                      </div>
                      <div v-if="inmail.text" class="linkedin-inmail-thread__message-text">
                        {{ inmail.text }}
                      </div>
                    </div>
                    <div class="linkedin-inmail-thread__message-meta">
                      <span class="linkedin-inmail-thread__message-time">
                        {{ formatInMailDate(inmail.sentAt) }}
                      </span>
                      <span class="linkedin-inmail-thread__message-status">
                        <Icon icon="mdi:check" />
                        Sent
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="sortedPendingInMails.length > 0"
                    class="linkedin-inmail-thread__waiting"
                  >
                    <Icon icon="mdi:clock-outline" />
                    <span>Waiting for reply...</span>
                  </div>
                </template>
              </div>

              <div class="linkedin-inmail-thread__compose">
                <Button
                  variant="primary"
                  size="sm"
                  icon="mdi:email-plus-outline"
                  :disabled="isLoadingInmailBalance || inmailCreditsCount === 0"
                  @click="showInmailModal = true"
                  :class="[
                    'linkedin-inmail-thread__send-btn',
                    inmailCreditsCount === 0 ? 'linkedin-inmail-thread__send-btn--disabled' : '',
                  ]"
                >
                  <template v-if="inmailCreditsCount === 0"> No InMail Credits Available </template>
                  <template v-else>
                    Send an InMail ({{ inmailCreditsCount }}
                    {{ inmailCreditsCount === 1 ? "credit" : "credits" }})
                  </template>
                </Button>
              </div>
            </div>
          </div>

          <div v-else class="linkedin-messages-content">
            <OutreachCueCards
              :icp-options="linkedInICPOptions"
              :icp-loading="props.icpLoading"
              :contact-id="selectedBucket?.contact?.id || ''"
              :contact-name="
                [selectedBucket?.contact?.fname, selectedBucket?.contact?.lname]
                  .filter(Boolean)
                  .join(' ')
              "
              @use-content="handleUseCueCardContent"
            />
            <div class="linkedin-chat-tabs">
              <button
                :class="['chat-tab', activeChatType !== 'inmail' ? 'active' : '']"
                @click="handleChatTypeChange('classic')"
              >
                <Icon icon="mdi:message-text-outline" class="chat-tab__icon" />
                Messages
              </button>
              <button
                :class="['chat-tab', activeChatType === 'inmail' ? 'active' : '']"
                @click="handleChatTypeChange('inmail')"
              >
                <Icon icon="mdi:email-fast-outline" class="chat-tab__icon" />
                InMail
              </button>
            </div>

            <div class="linkedin-message-view-wrapper">
              <div
                v-if="
                  (isLoadingLinkedInChat || isLoadingConversations) &&
                  linkedInConversations.length === 0
                "
                class="linkedin-empty-state"
              >
                <LoadingBars size="sm" />
              </div>

              <template v-else>
                <!-- ── STATE 3: Connection request sent, awaiting acceptance ── -->
                <div
                  v-if="
                    !isLinkedInConnected &&
                    activeChatType !== 'inmail' &&
                    sortedPendingConnectionNotes.length > 0
                  "
                  class="linkedin-connection-request-sent"
                >
                  <div class="linkedin-connection-request-sent__header">
                    <div class="linkedin-connection-request-sent__header-left">
                      <Icon
                        icon="mdi:account-clock-outline"
                        class="linkedin-connection-request-sent__icon"
                      />
                      <div class="linkedin-connection-request-sent__header-info">
                        <span class="linkedin-connection-request-sent__title"
                          >Connection Request Sent</span
                        >
                        <span class="linkedin-connection-request-sent__status"
                          >Awaiting acceptance</span
                        >
                      </div>
                    </div>
                  </div>

                  <div class="linkedin-connection-request-sent__messages">
                    <div
                      v-for="note in sortedPendingConnectionNotes"
                      :key="note.id"
                      class="linkedin-connection-request-sent__message"
                    >
                      <div class="linkedin-connection-request-sent__message-content">
                        <div class="linkedin-connection-request-sent__message-text">
                          {{ note.message }}
                        </div>
                      </div>
                      <div class="linkedin-connection-request-sent__message-meta">
                        <span class="linkedin-connection-request-sent__message-time">
                          {{ formatConnectionNoteDate(note.sentAt) }}
                        </span>
                        <span class="linkedin-connection-request-sent__message-status">
                          <Icon icon="mdi:check" />
                          Sent
                        </span>
                      </div>
                    </div>

                    <div class="linkedin-connection-request-sent__waiting">
                      <Icon icon="mdi:clock-outline" />
                      <span
                        >Waiting for {{ selectedBucket?.contact?.firstName || "them" }} to
                        accept...</span
                      >
                    </div>
                  </div>
                </div>

                <!-- ── STATE 2: Never connected, no message history ── -->
                <div
                  v-else-if="
                    !isLinkedInConnected &&
                    activeChatType !== 'inmail' &&
                    filteredLinkedInConversations.length === 0
                  "
                  class="linkedin-not-connected-state"
                >
                  <div class="linkedin-empty-state-icon">
                    <Icon icon="lucide:user-x" />
                  </div>
                  <h4 class="linkedin-empty-state-heading">Not Connected</h4>
                  <p class="linkedin-empty-state-text">
                    Send a connection request to start a LinkedIn conversation.
                  </p>
                </div>

                <!-- ── STATE 1: Was connected, has message history, now disconnected ── -->
                <template v-else-if="!isLinkedInConnected && activeChatType !== 'inmail'">
                  <div class="linkedin-was-connected-notice">
                    <Icon icon="lucide:user-minus" class="linkedin-was-connected-notice__icon" />
                    <span class="linkedin-was-connected-notice__text">
                      You haven't connected with
                      <strong>{{ selectedBucket?.contact?.firstName || "this contact" }}</strong>
                    </span>
                  </div>
                  <LinkedInMessageView
                    :conversation="computedSelectedLinkedInConversation"
                    :messages="linkedInMessages"
                    :channel-status="linkedInChannel?.status"
                    :channel="linkedInChannel"
                    :is-loading="isLoadingMessages"
                    :is-loading-older-messages="isLoadingOlderLinkedInMessages"
                    :has-more-messages="hasMoreLinkedInMessages"
                    :highlight-message-id="props.initialMessageId"
                    @reply="handleLinkedInReply"
                    @ai-reply="handleLinkedInAiReply"
                    @forward="handleLinkedInForward"
                    @delete-message="handleDeleteMessage"
                    @react="handleReactToMessage"
                    @remove-reaction="handleRemoveReaction"
                    @reconnect-requested="handleReconnectRequested"
                    @message-received="handleLinkedInMessageReceived"
                    @load-older-messages="handleLoadOlderLinkedInMessages"
                  />
                </template>

                <!-- ── INMAIL TAB: composition or empty state ── -->
                <div
                  v-else-if="
                    shouldShowInMailComposition ||
                    (activeChatType === 'inmail' && !computedSelectedLinkedInConversation)
                  "
                  class="linkedin-inmail-thread"
                >
                  <div class="linkedin-inmail-thread__header">
                    <div class="linkedin-inmail-thread__header-left">
                      <Icon icon="skill-icons:linkedin" class="linkedin-inmail-thread__icon" />
                      <div class="linkedin-inmail-thread__header-info">
                        <span class="linkedin-inmail-thread__title">InMail</span>
                        <span
                          :class="[
                            'linkedin-inmail-thread__status',
                            isLinkedInConnected
                              ? 'linkedin-inmail-thread__status--connected'
                              : 'linkedin-inmail-thread__status--disconnected',
                          ]"
                        >
                          {{ isLinkedInConnected ? "Connected" : "Not Connected" }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-if="isLoadingInmailBalance"
                      class="linkedin-inmail-thread__credits-skeleton"
                    />
                    <div
                      v-else-if="inmailBalance !== null"
                      :class="[
                        'linkedin-inmail-thread__credits',
                        inmailCreditsCount === 0 ? 'linkedin-inmail-thread__credits--empty' : '',
                      ]"
                    >
                      <span class="credits-value">{{ inmailCreditsCount }}</span>
                      <span class="credits-label">credits</span>
                    </div>
                  </div>

                  <div class="linkedin-inmail-thread__messages" ref="inmailMessagesContainer">
                    <div
                      v-if="isLoadingInmailBalance && sortedPendingInMails.length === 0"
                      class="linkedin-inmail-thread__loading"
                    >
                      <LoadingBars size="sm" />
                    </div>
                    <template v-else>
                      <div
                        v-if="sortedPendingInMails.length === 0"
                        class="linkedin-inmail-thread__empty"
                      >
                        <div class="linkedin-inmail-thread__empty-icon-wrap">
                          <Icon
                            icon="mdi:email-outline"
                            class="linkedin-inmail-thread__empty-icon"
                          />
                        </div>
                        <h4 class="linkedin-inmail-thread__empty-heading">
                          {{ isLinkedInConnected ? "No Messages Yet" : "Not Connected" }}
                        </h4>
                        <p class="linkedin-inmail-thread__empty-text">
                          <template v-if="isLinkedInConnected && inmailCreditsCount === 0">
                            You have no InMail credits remaining. Credits typically refresh monthly.
                          </template>
                          <template v-else-if="isLinkedInConnected">
                            Send an InMail to start a conversation with this contact.
                          </template>
                          <template v-else>
                            You're not connected on LinkedIn. Use InMail to reach out directly.
                          </template>
                        </p>
                      </div>

                      <div
                        v-for="inmail in sortedPendingInMails"
                        :key="inmail.id"
                        class="linkedin-inmail-thread__message linkedin-inmail-thread__message--sent"
                      >
                        <div class="linkedin-inmail-thread__message-content">
                          <div class="linkedin-inmail-thread__message-subject">
                            {{ inmail.subject }}
                          </div>
                          <div v-if="inmail.text" class="linkedin-inmail-thread__message-text">
                            {{ inmail.text }}
                          </div>
                        </div>
                        <div class="linkedin-inmail-thread__message-meta">
                          <span class="linkedin-inmail-thread__message-time">
                            {{ formatInMailDate(inmail.sentAt) }}
                          </span>
                          <span class="linkedin-inmail-thread__message-status">
                            <Icon icon="mdi:check" />
                            Sent
                          </span>
                        </div>
                      </div>

                      <div
                        v-if="sortedPendingInMails.length > 0"
                        class="linkedin-inmail-thread__waiting"
                      >
                        <Icon icon="mdi:clock-outline" />
                        <span>Waiting for reply...</span>
                      </div>
                    </template>
                  </div>

                  <div class="linkedin-inmail-thread__compose">
                    <Button
                      variant="primary"
                      size="sm"
                      icon="mdi:email-plus-outline"
                      :disabled="isLoadingInmailBalance || inmailCreditsCount === 0"
                      @click="showInmailModal = true"
                      :class="[
                        'linkedin-inmail-thread__send-btn',
                        inmailCreditsCount === 0
                          ? 'linkedin-inmail-thread__send-btn--disabled'
                          : '',
                      ]"
                    >
                      <template v-if="inmailCreditsCount === 0">
                        No InMail Credits Available
                      </template>
                      <template v-else>
                        Send an InMail ({{ inmailCreditsCount }}
                        {{ inmailCreditsCount === 1 ? "credit" : "credits" }})
                      </template>
                    </Button>
                  </div>
                </div>

                <!-- ── CONNECTED: no conversation yet — compose the first message below ── -->
                <div v-else-if="!computedSelectedLinkedInConversation" class="linkedin-empty-state">
                  <p>
                    No conversation yet. Send a message below to start one with
                    {{ selectedBucket?.contact?.firstName || "this contact" }}.
                  </p>
                </div>

                <!-- ── CONNECTED: normal message view ── -->
                <template v-else-if="computedSelectedLinkedInConversation">
                  <div
                    v-if="computedSelectedLinkedInConversation?.isInMail"
                    class="linkedin-inmail-chip"
                  >
                    <span>InMail</span>
                  </div>
                  <LinkedInMessageView
                    :conversation="computedSelectedLinkedInConversation"
                    :messages="linkedInMessages"
                    :channel-status="linkedInChannel?.status"
                    :channel="linkedInChannel"
                    :is-loading="isLoadingMessages"
                    :is-loading-older-messages="isLoadingOlderLinkedInMessages"
                    :has-more-messages="hasMoreLinkedInMessages"
                    :highlight-message-id="props.initialMessageId"
                    @reply="handleLinkedInReply"
                    @ai-reply="handleLinkedInAiReply"
                    @forward="handleLinkedInForward"
                    @delete-message="handleDeleteMessage"
                    @react="handleReactToMessage"
                    @remove-reaction="handleRemoveReaction"
                    @reconnect-requested="handleReconnectRequested"
                    @message-received="handleLinkedInMessageReceived"
                    @load-older-messages="handleLoadOlderLinkedInMessages"
                  />
                </template>
              </template>
              <AgentReplyDraft
                v-if="composerConversation && !shouldShowInMailComposition"
                :contact-id="selectedBucket?.contact?.id"
                channel="linkedin"
                @use="useReplyDraft"
              />
              <MessageComposer
                v-if="composerConversation && !shouldShowInMailComposition"
                :key="`linkedin-${computedSelectedLinkedInConversation?.id || 'none'}`"
                ref="messageComposerRef"
                :conversation="composerConversation"
                :reply-to="replyingTo"
                :channel-status="
                  linkedInChannel && linkedInChannel.status ? linkedInChannel.status : undefined
                "
                :show-ai-buttons="true"
                :ai-buttons-disabled="
                  !isChannelHealthyStatus(linkedInChannel?.status) ||
                  !isLinkedInConnected ||
                  isUsageBlocked
                "
                :is-generating-a-i="isGeneratingAI"
                :generating-action="generatingAction"
                :contact-id="selectedBucket?.contact?.id"
                :icp-id="icps?.find((i) => i.isDefault)?.id"
                :icps="icps"
                :channel-name="'linkedin'"
                @send-message="handleSendMessage"
                @cancel-reply="replyingTo = null"
                @ai-reply="handleLinkedInAiReply"
                @ai-followup="handleLinkedInAiFollowup"
                @ai-compose="handleLinkedInAiCompose"
              />
            </div>
          </div>
        </div>
        <LinkedInProfileTab
          v-else-if="activeLinkedInTab === 'profile'"
          :profile="linkedInProfileData"
          :loading="linkedInProfileLoading"
          :error="linkedInProfileError"
          :throttled-sections="linkedInProfile?.throttled_sections || []"
          @retry="loadLinkedInProfile"
        />
        <LinkedInActivitiesTab
          v-else-if="activeLinkedInTab === 'activities'"
          :identifier="linkedInIdentifier"
          :account-id="linkedInChannel?.accountId"
          :followers="linkedInProfileFollowers"
          :cache="linkedInActivitiesCache"
          @cache-update="handleActivitiesCacheUpdate"
        />
      </div>
    </div>

    <LinkedInConnectionRequestModal
      :show="showLinkedInConnectionModal"
      :contact-name="linkedInConnectionContactName"
      :contact-id="selectedBucket?.contact?.id"
      :icp-id="icps?.find((i) => i.isDefault)?.id"
      :is-loading="isSendingLinkedInInvite"
      :error="linkedInInviteError"
      :initial-message="linkedInConnectionInitialMessage"
      @close="handleLinkedInConnectionModalClose"
      @send="handleLinkedInConnectionSend"
    />
    <InMailCompositionModal
      :show="showInmailModal"
      :contact="selectedBucket?.contact"
      :account-id="linkedInChannel?.accountId"
      :provider-id="linkedInProviderId"
      :inmail-balance="inmailBalance"
      :icp-id="icps?.find((i) => i.isDefault)?.id"
      :initial-subject="inmailInitialSubject"
      :initial-body="inmailInitialBody"
      @close="
        showInmailModal = false;
        inmailInitialSubject = '';
        inmailInitialBody = '';
      "
      @sent="handleInmailSent"
    />
    <DeleteConfirmationModal
      v-if="showDeleteConfirmation"
      entity="message"
      :delete-function="confirmDeleteMessage"
      @close-delete-confirmation-modal="handleDeleteConfirmationClose"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { Icon } from "@iconify/vue";
import LinkedInMessageView from "@/components/socials/LinkedInMessageView.vue";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import MessageComposer from "@/components/socials/MessageComposer.vue";
import AgentReplyDraft from "@/components/agent/AgentReplyDraft.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";
import { normalizeLinkedInMessages } from "@/utils/conversationNormalizer";
import LinkedInProfileTab from "@/components/bucket/linkedin/LinkedInProfileTab.vue";
import LinkedInActivitiesTab from "@/components/bucket/linkedin/LinkedInActivitiesTab.vue";
import LinkedInConnectionRequestModal from "@/components/bucket/linkedin/LinkedInConnectionRequestModal.vue";
import InMailCompositionModal from "@/components/bucket/email/InMailCompositionModal.vue";
import OutreachCueCards from "@/components/bucket/ai/OutreachCueCards.vue";
import { Button } from "@/components/ui";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import Unipile from "@/services/unipile/unipile";
import { useQueryClient } from "@tanstack/vue-query";
import { useBillingStatus } from "@/composables/billing/useBillingStatus";
import {
  useMessageWebhooks,
  matchesConversation,
  createMessageFromPayload,
  checkMessageExists,
} from "@/composables/messages/useMessageWebhooks";
import {
  useLinkedInChatByContact,
  useLinkedInConversationMessages,
  useLinkedInProfile,
  useLinkedInConnectionStatus,
  useLinkedInInmailBalance,
  useSendLinkedInMessage,
  useSendLinkedInConnectionRequest,
  useDeleteLinkedInMessage,
} from "@/composables/linkedin";
import { useReactToUnipileMessage } from "@/composables/useReactToUnipileMessage";

defineOptions({
  name: "LinkedInInteractionPanel",
});

const props = defineProps({
  selectedBucket: {
    type: Object,
    required: true,
  },
  linkedInChannel: {
    type: Object,
    default: null,
  },
  isChannelsLoading: {
    type: Boolean,
    default: false,
  },
  icps: {
    type: Array,
    default: () => [],
  },
  icpLoading: {
    type: Boolean,
    default: false,
  },
  pendingCueCardContent: {
    type: Object,
    default: null,
  },
  initialConversationId: {
    type: String,
    default: null,
  },
  initialMessageId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  "refresh-conversations",
  "message-sent",
  "show-edit-contact-modal",
  "reconnect-requested",
  "open-ai-personalization",
  "switch-channel",
  "content-applied",
]);

const $toast = useToast();
const resolvedAiPromptsQuery = useResolvedAiPrompts();

const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

// Template refs
const inmailMessagesContainer = ref(null);
const queryClient = useQueryClient();

// Local state - UI state
const activeLinkedInTab = ref("messages");
const activeChatType = ref("classic");
const selectedLinkedInConversationId = ref(null);
const showLinkedInDetailView = ref(false);
const selectedLinkedInConversation = ref(null);
const replyingTo = ref(null);
const showLinkedInConnectionModal = ref(false);
const linkedInConnectionInitialMessage = ref("");
const showInmailModal = ref(false);
const inmailInitialSubject = ref("");
const inmailInitialBody = ref("");
const linkedInActivitiesCache = ref({});
const linkedInActivitiesLoaded = ref(false);
// Tracks, per chat id, the unreadCount we last issued a setReadStatus call for — avoids
// re-firing on every refetch of chatByContactData that doesn't reflect new activity (e.g.
// after sending a message), while still re-firing once a new inbound message bumps the
// chat's unreadCount back up.
const markedReadChatUnreadCounts = new Map();
const isGeneratingAI = ref(false);
const generatingAction = ref(null);
const messageComposerRef = ref(null);

// Local state - Loading flags
const isLoadingMessages = ref(false);
const isLoadingConversations = ref(false);
const isLoadingLinkedInChat = computed(() => isLoadingChatByContact.value);
const isLoadingOlderLinkedInMessages = ref(false);
const isSendingMessage = ref(false);
const isCheckingConnection = computed(() => isLoadingConnectionStatus.value);
const isLoadingInmailBalance = computed(() => isLoadingInmailBalanceQuery.value);

// Local state - Data
const linkedInMessagesData = ref([]);
const linkedInConversations = ref([]);
const hasMoreLinkedInMessages = ref(true);
const oldestLinkedInMessageTimestamp = ref(null);
const linkedInProfile = ref(null);
const linkedInProfileError = ref(null);
const linkedInInviteError = ref(null);
const isLinkedInConnected = ref(false);
const canSendInMail = ref(false);
const inmailBalance = ref(null);
const linkedInProviderId = ref(null);
const pendingInMails = ref([]);
const pendingConnectionNotes = ref([]);
const invitationStatus = ref(null);
const chatAccessError = ref(null);
const messageWebhooksCleanup = ref(null);

// Computed params for queries
const chatByContactParams = computed(() => {
  const contactId = props.selectedBucket?.contact?.id;
  const accountId = props.linkedInChannel?.accountId;
  if (!contactId || !accountId) return null;
  return {
    contactId: String(contactId),
    channel: "linkedin",
    accountId,
  };
});

const conversationMessagesParams = computed(() => {
  if (!selectedLinkedInConversationId.value) return null;
  return {
    conversationId: selectedLinkedInConversationId.value,
    limit: 10,
    before: isLoadingOlderLinkedInMessages.value ? oldestLinkedInMessageTimestamp.value : null,
  };
});

const profileParams = computed(() => {
  const linkedInUrl = props.selectedBucket?.contact?.linkedInUrl;
  if (!linkedInUrl || !props.linkedInChannel?.accountId) return null;
  const identifier = extractLinkedInIdentifierFromUrl(linkedInUrl);
  if (!identifier) return null;
  return {
    identifier,
    accountId: props.linkedInChannel.accountId,
  };
});

const connectionStatusParams = computed(() => {
  const linkedInUrl = props.selectedBucket?.contact?.linkedInUrl;
  if (!linkedInUrl || !props.linkedInChannel?.accountId) return null;
  const identifier = extractLinkedInIdentifierFromUrl(linkedInUrl);
  if (!identifier) return null;
  return {
    accountId: props.linkedInChannel.accountId,
    identifier,
  };
});

const inmailBalanceParams = computed(() => {
  if (!props.linkedInChannel?.accountId) return null;
  return {
    accountId: props.linkedInChannel.accountId,
  };
});

// TanStack Query composables
const {
  data: chatByContactData,
  isLoading: isLoadingChatByContact,
  refetch: refetchChatByContact,
} = useLinkedInChatByContact(chatByContactParams);

const {
  data: conversationMessagesData,
  isLoading: isLoadingConversationMessages,
  refetch: refetchConversationMessages,
} = useLinkedInConversationMessages(conversationMessagesParams);

const {
  data: profileData,
  isLoading: isLoadingProfileQuery,
  error: profileQueryError,
  refetch: refetchProfile,
} = useLinkedInProfile(profileParams);

const {
  data: connectionStatusData,
  isLoading: isLoadingConnectionStatus,
  refetch: refetchConnectionStatus,
} = useLinkedInConnectionStatus(connectionStatusParams);

const {
  data: inmailBalanceData,
  isLoading: isLoadingInmailBalanceQuery,
  refetch: refetchInmailBalance,
} = useLinkedInInmailBalance(inmailBalanceParams);

const { sendMessage: sendLinkedInMessage, isSending: isSendingLinkedInMessage } =
  useSendLinkedInMessage();

const {
  sendConnectionRequest: sendLinkedInConnectionRequest,
  isSending: isSendingLinkedInConnectionRequest,
} = useSendLinkedInConnectionRequest();

const isSendingLinkedInInvite = isSendingLinkedInConnectionRequest;

const { deleteMessage: deleteLinkedInMessage, isDeleting: isDeletingLinkedInMessage } =
  useDeleteLinkedInMessage();

const { addReaction: addLinkedInReaction, removeReaction: removeLinkedInReaction } =
  useReactToUnipileMessage();

// Computed properties
const linkedInMessages = computed(() => {
  return linkedInMessagesData.value || [];
});

const computedSelectedLinkedInConversation = computed(() => {
  if (selectedLinkedInConversation.value) {
    return selectedLinkedInConversation.value;
  }
  if (!selectedLinkedInConversationId.value) {
    return filteredLinkedInConversations.value?.[0] || null;
  }
  return (
    filteredLinkedInConversations.value?.find(
      (c) => c.id === selectedLinkedInConversationId.value
    ) ||
    filteredLinkedInConversations.value?.[0] ||
    null
  );
});

// MessageComposer (and SocialMessageComposer beneath it) key their entire render — whether
// to show the social composer at all, and whether sending is allowed — off `conversation` and
// `conversation.channel`. When we're connected but have no chat yet, computedSelectedLinkedInConversation
// is legitimately null, but passing that through renders nothing at all (see handleSendMessage's
// no-conversation branch for where the real send actually goes). Give the composer a minimal
// stand-in conversation so it renders and unblocks sending; it's never used for anything besides
// satisfying the composer's own render/guard checks.
const composerConversation = computed(() => {
  if (computedSelectedLinkedInConversation.value) {
    return computedSelectedLinkedInConversation.value;
  }
  if (!isLinkedInConnected.value || activeChatType.value === "inmail") {
    return null;
  }
  return {
    id: "",
    contactName: [props.selectedBucket?.contact?.fname, props.selectedBucket?.contact?.lname]
      .filter(Boolean)
      .join(" "),
    channel: "linkedin",
    lastMessage: "",
    lastMessageAt: "",
    unreadCount: 0,
  };
});

const linkedInICPOptions = computed(() => {
  const list = Array.isArray(props.icps) ? props.icps : [];
  return [
    { value: "", label: "Select ICP for LinkedIn" },
    ...list.map((icp) => ({
      value: icp.id,
      label: icp.isDefault ? `${icp.name} (Default)` : icp.name,
    })),
  ];
});

const linkedInIdentifier = computed(() => {
  if (linkedInProfile.value?.provider_id) {
    return linkedInProfile.value.provider_id;
  }
  const linkedInUrl = props.selectedBucket?.contact?.linkedInUrl;
  if (!linkedInUrl) return null;
  return extractLinkedInIdentifierFromUrl(linkedInUrl);
});

const linkedInProfileFollowers = computed(() => {
  return linkedInProfile.value?.follower_count || linkedInProfile.value?.followers_count || 0;
});

const linkedInProfileData = computed(() => {
  if (!linkedInProfile.value) return null;

  const profile = linkedInProfile.value;
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "";

  const certifications = (profile.certifications || []).map((cert) => ({
    name: cert.name || "",
    issuing_organization: cert.issuing_organization || cert.authority || "",
    issuingOrganization: cert.issuing_organization || cert.authority || "",
    issue_date:
      cert.issue_date ||
      (cert.start_date
        ? `${cert.start_date.month || 1}/${cert.start_date.day || 1}/${cert.start_date.year}`
        : undefined),
    issueDate:
      cert.issue_date ||
      (cert.start_date
        ? `${cert.start_date.month || 1}/${cert.start_date.day || 1}/${cert.start_date.year}`
        : undefined),
    expiration_date:
      cert.expiration_date ||
      (cert.end_date
        ? `${cert.end_date.month || 1}/${cert.end_date.day || 1}/${cert.end_date.year}`
        : null),
    expirationDate:
      cert.expiration_date ||
      (cert.end_date
        ? `${cert.end_date.month || 1}/${cert.end_date.day || 1}/${cert.end_date.year}`
        : null),
    credential_id: cert.credential_id || cert.license_number || "",
    credential_url: cert.credential_url || cert.url || "",
    url: cert.credential_url || cert.url || "",
  }));

  return {
    contactName: fullName,
    contactRole: profile.headline || "",
    location: profile.location || "",
    summary: profile.summary || "",
    profilePictureUrl: profile.profile_picture_url_large || profile.profile_picture_url || "",
    workExperience: profile.work_experience || [],
    education: profile.education || [],
    skills: profile.skills || [],
    languages: profile.languages || [],
    certifications: certifications,
    projects: profile.projects || [],
    volunteeringExperience: profile.volunteering_experience || [],
    connectionsCount: profile.connections_count,
    followersCount: profile.follower_count || profile.followers_count,
    isPremium: profile.is_premium,
    networkDistance: profile.network_distance,
    throttledSections: profile.throttled_sections || [],
  };
});

const canSendLinkedInInvite = computed(() => {
  const linkedInUrl = props.selectedBucket?.contact?.linkedInUrl;
  const channelOk = isChannelHealthyStatus(props.linkedInChannel?.status);
  return (
    !!linkedInUrl && !!props.linkedInChannel?.accountId && channelOk && !isLinkedInConnected.value
  );
});

const linkedInConnectionContactName = computed(() => {
  const c = props.selectedBucket?.contact;
  if (!c) return null;
  const first = c.firstName ?? c.fname ?? "";
  const last = c.lastName ?? c.lname ?? "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || null;
});

const isLinkedInChannelDisconnected = computed(() => {
  if (!props.linkedInChannel) return true;
  if (!props.linkedInChannel.isConnected) return true;
  const status = props.linkedInChannel.status?.toUpperCase();
  return ["DISCONNECTED", "ERROR"].includes(status || "");
});

const sortedPendingInMails = computed(() => {
  if (computedSelectedLinkedInConversation.value) {
    return [];
  }

  if (!pendingInMails.value || pendingInMails.value.length === 0) {
    return [];
  }

  return [...pendingInMails.value]
    .filter((inmail) => inmail.status === "pending")
    .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
});

const sortedPendingConnectionNotes = computed(() => {
  if (computedSelectedLinkedInConversation.value) {
    return [];
  }

  if (!pendingConnectionNotes.value || pendingConnectionNotes.value.length === 0) {
    return [];
  }

  return [...pendingConnectionNotes.value]
    .filter((note) => note.status === "pending")
    .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
});

const connectionStatusBadge = computed(() => {
  if (!props.selectedBucket?.contact?.linkedInUrl) return null;
  if (isCheckingConnection.value || isLoadingConnectionStatus.value) return "loading";
  if (isLinkedInConnected.value) return "connected";
  if (invitationStatus.value?.type === "SENT" && invitationStatus.value?.status === "PENDING") {
    return "pending";
  }
  return "connect";
});

const filteredLinkedInConversations = computed(() => {
  if (activeChatType.value === "inmail") {
    return linkedInConversations.value.filter(
      (conv) => conv.isInMail === true || conv.content_type === "inmail"
    );
  }
  return linkedInConversations.value.filter(
    (conv) => !conv.isInMail && conv.content_type !== "inmail"
  );
});

const hasInMailConversation = computed(() => {
  if (!computedSelectedLinkedInConversation.value) return false;
  return (
    computedSelectedLinkedInConversation.value.isInMail === true ||
    computedSelectedLinkedInConversation.value.content_type === "inmail"
  );
});

const shouldShowInMailComposition = computed(() => {
  if (hasInMailConversation.value) return false;
  if (activeChatType.value === "inmail") return true;
  return false;
});

const inmailCreditsCount = computed(() => getTotalInmailCredits(inmailBalance.value));

const linkedInProfileLoading = computed(() => {
  return isLoadingProfileQuery.value;
});
// Helper functions
function extractLinkedInIdentifierFromUrl(url) {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    const inMatch = path.match(/\/in\/([^/]+)/i);
    if (inMatch?.[1]) return decodeURIComponent(inMatch[1]);

    const companyMatch = path.match(/\/company\/([^/]+)/i);
    if (companyMatch?.[1]) return decodeURIComponent(companyMatch[1]);

    const salesPeopleMatch = path.match(/\/sales\/people\/([^/]+)/i);
    if (salesPeopleMatch?.[1]) return decodeURIComponent(salesPeopleMatch[1]);

    const parts = path.split("/").filter(Boolean);
    if (parts.length === 0) return null;
    return decodeURIComponent(parts[parts.length - 1]);
  } catch (error) {
    console.error("Failed to parse LinkedIn URL:", error);
    return null;
  }
}

function isChannelHealthyStatus(status) {
  if (!status) return false;
  const upperStatus = status.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(upperStatus);
}

function getTotalInmailCredits(balance) {
  if (!balance) return 0;
  if (balance.activeCredits !== undefined) {
    return balance.activeCredits;
  }
  return (balance.premium || 0) + (balance.recruiter || 0) + (balance.sales_navigator || 0);
}

function formatInMailDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function formatConnectionNoteDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function formatOptimisticLinkMessage(htmlText) {
  if (!htmlText || typeof htmlText !== "string") {
    return htmlText || "";
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlText;
  const links = tempDiv.querySelectorAll("a[href]");

  if (links.length === 1) {
    const link = links[0];
    const url = link.getAttribute("href") || "";
    const allText = tempDiv.textContent.trim();
    const linkText = link.textContent.trim();

    if (
      url &&
      (allText === linkText || allText.replace(/\s+/g, " ") === linkText.replace(/\s+/g, " "))
    ) {
      const escapedUrl = url.replace(/"/g, "&quot;");
      return `Link (<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" style="color: white; text-decoration: underline;">${url}</a>)`;
    }
  }

  return htmlText;
}

watch(isLinkedInConnected, (newValue) => {
  if (!newValue) {
    activeChatType.value = "classic";
  } else if (linkedInConversations.value.length > 0 && !activeChatType.value) {
    activeChatType.value = "classic";
  }
});

// Watch chatByContactData to update local state
watch(
  () => chatByContactData.value,
  (chatData) => {
    if (!chatData) return;

    chatAccessError.value = null;
    isLinkedInConnected.value = chatData.isConnected === true;
    canSendInMail.value = chatData.canSendInMail === true;
    linkedInProviderId.value = chatData.providerId || null;
    pendingInMails.value = chatData.pendingInMails || [];
    pendingConnectionNotes.value = chatData.pendingConnectionNotes || [];
    if (chatData.invitationStatus) {
      invitationStatus.value = chatData.invitationStatus;
    }

    if (chatData.inmailBalance) {
      inmailBalance.value = chatData.inmailBalance;
    } else if (
      !isLinkedInConnected.value &&
      canSendInMail.value &&
      props.linkedInChannel?.accountId
    ) {
      refetchInmailBalance();
    }

    const allChats = chatData.chats || (chatData.chat ? [chatData.chat] : []);
    const selectedChat = chatData.selectedChat || chatData.chat || null;

    linkedInConversations.value = allChats.map((conv) => {
      const isInMail = conv.isInMail === true || conv.content_type === "inmail";
      return {
        ...conv,
        isInMail: isInMail,
        content_type: conv.content_type || (isInMail ? "inmail" : null),
      };
    });

    if (selectedChat) {
      showLinkedInDetailView.value = true;
      if (!activeLinkedInTab.value) {
        activeLinkedInTab.value = "messages";
      }
      const isInMailChat =
        selectedChat.isInMail === true ||
        selectedChat.content_type === "inmail" ||
        chatData.isInMail === true;
      selectedLinkedInConversation.value = {
        ...selectedChat,
        isInMail: isInMailChat,
        content_type: selectedChat.content_type || (isInMailChat ? "inmail" : null),
      };
      selectedLinkedInConversationId.value = selectedChat.id;
      linkedInMessagesData.value = chatData.messages || [];
      hasMoreLinkedInMessages.value = chatData.hasMore || false;
      oldestLinkedInMessageTimestamp.value = chatData.oldestTimestamp || null;

      if (isLinkedInConnected.value) {
        activeChatType.value = isInMailChat ? "inmail" : "classic";
      }

      // This is the primary load path — it never goes through
      // handleLinkedInConversationSelected below, so the mark-as-read has to happen here
      // too or the Activity column count never clears.
      markContactChatsRead([selectedChat, ...allChats]);
    } else {
      linkedInConversations.value = [];
      selectedLinkedInConversation.value = null;
      selectedLinkedInConversationId.value = null;
      showLinkedInDetailView.value = true;
      if (isLinkedInConnected.value) {
        activeChatType.value = "classic";
      }
    }
  },
  { immediate: true }
);

// Watch connection status data
watch(
  () => connectionStatusData.value,
  (data) => {
    if (data?.isConnected !== undefined) {
      isLinkedInConnected.value = data.isConnected;
    }
  }
);

// Watch profile data
watch(
  () => profileData.value,
  (data) => {
    if (data) {
      linkedInProfile.value = data;
      linkedInProfileError.value = null;
    }
  }
);

watch(
  () => profileQueryError.value,
  (error) => {
    if (error) {
      linkedInProfileError.value = error.message || "Failed to load profile";
      linkedInProfile.value = null;
    }
  }
);

// Watch inmail balance data
watch(
  () => inmailBalanceData.value,
  (data) => {
    if (data) {
      inmailBalance.value = data;
    }
  }
);

// Watch initialConversationId to auto-select conversation, e.g. from clicking an
// Interaction History entry. The target conversation may not be in the currently
// loaded list (older chats, or the list simply hasn't loaded yet), so once the list
// has settled without a match, fetch that one conversation directly by id.
let lastAppliedLinkedInConversationId = null;
watch(
  [() => linkedInConversations.value, () => props.initialConversationId, isLoadingConversations],
  async ([conversations, initialId, loadingConversations]) => {
    if (!initialId || initialId === lastAppliedLinkedInConversationId) return;

    const match = conversations?.find((c) => c.unipileChatId === initialId || c.id === initialId);
    if (match) {
      handleLinkedInConversationSelected(match);
      lastAppliedLinkedInConversationId = initialId;
      return;
    }

    if (loadingConversations) return;
    const accountId = props.linkedInChannel?.accountId;
    if (!accountId) return;

    try {
      const response = await Unipile.getChat(initialId, accountId);
      if (response?.data?.data && initialId === props.initialConversationId) {
        handleLinkedInConversationSelected(response.data.data);
        lastAppliedLinkedInConversationId = initialId;
      }
    } catch (error) {
      console.error("Failed to load LinkedIn conversation from Interaction History", error);
    }
  },
  { immediate: true }
);

// Methods
const handleActivitiesCacheUpdate = (cacheData) => {
  const cacheKey = `${linkedInIdentifier.value}-${props.linkedInChannel?.accountId}`;
  if (cacheKey) {
    linkedInActivitiesCache.value[cacheKey] = cacheData;
  }
};

const handleLinkedInTabClick = (tab) => {
  if (activeLinkedInTab.value !== tab) {
    activeLinkedInTab.value = tab;
  }
};

async function applyLinkedInContent(content) {
  await nextTick();
  if (messageComposerRef.value && typeof messageComposerRef.value.setContent === "function") {
    messageComposerRef.value.setContent(content);
  }
  $toast?.success("LinkedIn message loaded from cue card");
}

async function handleUseCueCardContent(payload) {
  if (payload.channel === "linkedin_connection") {
    if (isLinkedInConnected.value) {
      activeLinkedInTab.value = "messages";
      await applyLinkedInContent(payload.content);
    } else {
      linkedInConnectionInitialMessage.value = payload.content || "";
      linkedInInviteError.value = null;
      showLinkedInConnectionModal.value = true;
    }
  } else if (payload.channel === "linkedin_followup") {
    activeLinkedInTab.value = "messages";
    await applyLinkedInContent(payload.content);
  } else if (payload.channel === "linkedin_inmail") {
    inmailInitialSubject.value = payload.subject || "";
    inmailInitialBody.value = payload.content || "";
    showInmailModal.value = true;
  } else {
    emit("switch-channel", payload);
  }
}

// Marks every one of the contact's chats read when the tab loads — not just the
// auto-selected newest one — since the Activity column sums unread across all of
// them, so a second conversation (e.g. a separate InMail thread) would otherwise keep
// the count stuck. Skips chats already marked at their current unreadCount.
function markContactChatsRead(chats) {
  const seen = new Set();
  const toMark = chats.filter((chat) => {
    if (!chat?.id || seen.has(chat.id)) return false;
    seen.add(chat.id);
    return markedReadChatUnreadCounts.get(chat.id) !== (chat.unreadCount ?? 0);
  });
  if (toMark.length === 0) return;

  for (const chat of toMark) markedReadChatUnreadCounts.set(chat.id, chat.unreadCount ?? 0);

  const contactId = props.selectedBucket?.contact?.id;
  Promise.allSettled(
    toMark.map((chat) =>
      Unipile.performChatAction(chat.id, { action: "setReadStatus", value: true }).catch(
        (error) => {
          console.error("Failed to mark LinkedIn conversation as read:", error);
          markedReadChatUnreadCounts.delete(chat.id);
        }
      )
    )
  ).then(() => {
    // Both badges are separate queries: the Buckets table's Activity column and this
    // modal's LinkedIn tab badge.
    queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
    if (contactId) {
      queryClient.invalidateQueries({ queryKey: ["interactionCounts", contactId] });
    }
  });
}

const handleLinkedInConversationSelected = async (conversation) => {
  selectedLinkedInConversationId.value = conversation.id;
  selectedLinkedInConversation.value = conversation;
  showLinkedInDetailView.value = true;

  if (isLinkedInConnected.value && conversation) {
    const isInMail = conversation.isInMail === true || conversation.content_type === "inmail";
    activeChatType.value = isInMail ? "inmail" : "classic";
  }

  try {
    const response = await Unipile.getChat(conversation.id);
    if (response?.data?.data) {
      const fullConversation = response.data.data;
      const isInMail =
        fullConversation.isInMail ||
        conversation.isInMail ||
        fullConversation.content_type === "inmail";
      selectedLinkedInConversation.value = {
        ...conversation,
        ...fullConversation,
        participants: fullConversation.participants || conversation.participants || [],
        isInMail: isInMail,
      };
    }
  } catch (error) {
    console.error("Failed to fetch full conversation details:", error);
  }

  if (linkedInMessagesData.value.length === 0) {
    await loadMessagesForConversation(conversation.id, "linkedin");
  }

  const unread = conversation.unreadCount || conversation.unread_count || 0;
  const contactId = props.selectedBucket?.contact?.id;

  if (unread > 0 && contactId) {
    queryClient.setQueryData(["interactionCounts", contactId], (old) => {
      if (!old?.linkedin) return old;
      return {
        ...old,
        linkedin: {
          ...old.linkedin,
          unread: Math.max(0, (old.linkedin.unread || 0) - unread),
        },
      };
    });
  }

  // Always mark the chat read on open, regardless of what Unipile's freshly-fetched
  // conversation list reports as `unread`. That value can be out of sync with our own
  // chat.unreadCount (which only ever increments on inbound-message webhooks) — gating this
  // call on it meant a chat Unipile already considered "read" would never trigger our own
  // reset, leaving the Activity column's count stuck forever. performChatAction is a safe
  // no-op when the chat is already marked read.
  try {
    await Unipile.performChatAction(conversation.id, {
      action: "setReadStatus",
      value: true,
    });
    // Buckets table's Activity column is a separate bulk query, keyed by the page's
    // contactIds — the backend already zeroes chat.unreadCount on setReadStatus, but that
    // query's cache is untouched by the optimistic update above, so invalidate it too.
    queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
  } catch (error) {
    console.error("Failed to mark LinkedIn conversation as read:", error);
    if (contactId) {
      queryClient.invalidateQueries({ queryKey: ["interactionCounts", contactId] });
    }
  }
};

const handleLinkedInReply = (message) => {
  replyingTo.value = message;
};

// Puts the AI's suggested reply in the message box for the rep to rewrite and send.
const useReplyDraft = ({ text }) => {
  try {
    messageComposerRef.value?.setContent?.(text);
  } catch (err) {
    console.warn("Error updating message composer:", err);
  }
};

const handleLinkedInAiReply = async (payload) => {
  if (!computedSelectedLinkedInConversation.value || isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "reply";
  try {
    const conversationHistory = normalizeLinkedInMessages(linkedInMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateReply({
      contactId,
      icpId,
      channel: "linkedin",
      conversationHistory,
    });

    await nextTick();
    if (messageComposerRef.value && typeof messageComposerRef.value.setContent === "function") {
      try {
        messageComposerRef.value.setContent(response.body);
      } catch (err) {
        console.warn("Error updating message composer:", err);
      }
    }

    $toast?.success("AI reply generated");
  } catch (error) {
    console.error("Failed to generate AI reply:", error);
    $toast?.error("AI generation failed");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
};

const handleLinkedInAiFollowup = async (payload) => {
  if (!computedSelectedLinkedInConversation.value || isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "followup";
  try {
    const conversationHistory = normalizeLinkedInMessages(linkedInMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateFollowup({
      contactId,
      icpId,
      channel: "linkedin",
      conversationHistory,
    });

    await nextTick();
    if (messageComposerRef.value && typeof messageComposerRef.value.setContent === "function") {
      try {
        messageComposerRef.value.setContent(response.body);
      } catch (err) {
        console.warn("Error updating message composer:", err);
      }
    }

    $toast?.success("AI follow-up generated");
  } catch (error) {
    console.error("Failed to generate AI follow-up:", error);
    $toast?.error("AI generation failed");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
};

const handleLinkedInAiCompose = async (payload) => {
  if (isGeneratingAI.value) return;

  isGeneratingAI.value = true;
  generatingAction.value = "compose";
  try {
    const conversationHistory = normalizeLinkedInMessages(linkedInMessages.value || []);

    const contactId = props.selectedBucket?.contact?.id;
    const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

    const response = await AIGenerationService.generateCompose({
      contactId,
      icpId,
      channel: "linkedin",
      conversationHistory,
      customInstructions: payload?.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });

    await nextTick();
    if (messageComposerRef.value && typeof messageComposerRef.value.setContent === "function") {
      try {
        messageComposerRef.value.setContent(response.body);
      } catch (err) {
        console.warn("Error updating message composer:", err);
      }
    }

    $toast?.success("AI message composed");
  } catch (error) {
    console.error("Failed to generate AI compose:", error);
    $toast?.error("AI generation failed");
  } finally {
    isGeneratingAI.value = false;
    generatingAction.value = null;
  }
};

const handleLinkedInForward = (message) => {
  $toast?.warning("Forwarding only available for WhatsApp");
};

const showDeleteConfirmation = ref(false);
const messageToDelete = ref(null);

const handleDeleteMessage = (message) => {
  messageToDelete.value = message;
  showDeleteConfirmation.value = true;
};

const confirmDeleteMessage = async () => {
  const message = messageToDelete.value;
  if (!message) return null;

  try {
    await deleteLinkedInMessage(message.id);
    const idx = linkedInMessagesData.value.findIndex((m) => m.id === message.id);
    if (idx !== -1) linkedInMessagesData.value[idx].deleted = true;
    $toast?.success("Message deleted successfully");
    return true;
  } catch (error) {
    console.error("Failed to delete message:", error);
    $toast?.error("Failed to delete message");
    throw error;
  }
};

const handleDeleteConfirmationClose = () => {
  showDeleteConfirmation.value = false;
  messageToDelete.value = null;
};

const handleReactToMessage = async (message, reaction) => {
  try {
    const existing = linkedInMessagesData.value.find((m) => m.id === message.id);
    const ownReaction = existing?.reactions?.find((r) => r.is_sender);
    const isRemoving = ownReaction?.value === reaction;

    const reactions = isRemoving
      ? await removeLinkedInReaction(message.id)
      : await addLinkedInReaction(message.id, reaction);

    const idx = linkedInMessagesData.value.findIndex((m) => m.id === message.id);
    if (idx !== -1) {
      linkedInMessagesData.value[idx].reactions = reactions;
    }
  } catch (error) {
    console.error("Failed to react to message:", error);
    $toast?.error("Failed to react to message");
  }
};

const handleRemoveReaction = async (message) => {
  try {
    const reactions = await removeLinkedInReaction(message.id);
    const idx = linkedInMessagesData.value.findIndex((m) => m.id === message.id);
    if (idx !== -1) {
      linkedInMessagesData.value[idx].reactions = reactions;
    }
  } catch (error) {
    console.error("Failed to remove reaction:", error);
    $toast?.error("Failed to remove reaction");
  }
};

const handleReconnectRequested = async (channel) => {};

const handleLinkedInMessageReceived = async (data) => {
  try {
    if (!computedSelectedLinkedInConversation.value) return;

    if (linkedInMessagesData.value.length === 0) {
      await loadMessagesForConversation(computedSelectedLinkedInConversation.value.id, "linkedin");
    }
  } catch (error) {
    console.error("Error handling LinkedIn message received:", error);
  }
};

const handleLoadOlderLinkedInMessages = () => {
  if (
    computedSelectedLinkedInConversation.value?.id &&
    hasMoreLinkedInMessages.value &&
    !isLoadingOlderLinkedInMessages.value
  ) {
    loadMessagesForConversation(computedSelectedLinkedInConversation.value.id, "linkedin", true);
  }
};

const refreshLinkedInConversationWithParticipants = async (conversationId) => {
  if (!conversationId) return;

  try {
    const response = await Unipile.getChat(conversationId);
    if (response?.data?.data) {
      const fullConversation = response.data.data;
      if (computedSelectedLinkedInConversation.value?.id === conversationId) {
        selectedLinkedInConversation.value = {
          ...computedSelectedLinkedInConversation.value,
          ...fullConversation,
          participants:
            fullConversation.participants ||
            computedSelectedLinkedInConversation.value.participants ||
            [],
        };
      }
    }
  } catch (error) {
    console.error("Failed to refresh conversation:", error);
  }
};

const loadConversations = async () => {
  if (!props.linkedInChannel) return;

  const crmContactId = props.selectedBucket?.contact?.id;

  if (crmContactId) {
    try {
      isLoadingConversations.value = true;

      await refetchChatByContact();
      emit("refresh-conversations");
    } catch (error) {
      console.error("Failed to load LinkedIn conversations:", error);
      const errorResponse = error?.response?.data;
      const statusCode = errorResponse?.statusCode || error.response?.status;

      if (statusCode === 404) {
        const rawMessage =
          errorResponse?.message ||
          errorResponse?.error?.message ||
          "Unable to access this contact's chat.";

        chatAccessError.value = rawMessage;

        if (errorResponse?.data) {
          pendingInMails.value = errorResponse.data.pendingInMails || [];
          pendingConnectionNotes.value = errorResponse.data.pendingConnectionNotes || [];
        } else if (errorResponse?.pendingInMails || errorResponse?.pendingConnectionNotes) {
          pendingInMails.value = errorResponse.pendingInMails || [];
          pendingConnectionNotes.value = errorResponse.pendingConnectionNotes || [];
        }

        showLinkedInDetailView.value = true;
      } else if (statusCode === 422) {
        const rawMessage =
          errorResponse?.message || errorResponse?.error?.message || "Recipient cannot be reached.";

        chatAccessError.value = rawMessage;
        showLinkedInDetailView.value = true;
      } else {
        const rawMessage =
          errorResponse?.message || errorResponse?.error?.message || "Failed to load chat";

        chatAccessError.value = rawMessage;
        showLinkedInDetailView.value = true;
        $toast?.error(rawMessage);
      }

      linkedInConversations.value = [];
      selectedLinkedInConversation.value = null;
      selectedLinkedInConversationId.value = null;
    } finally {
      isLoadingConversations.value = false;
    }
  } else {
    if (!props.linkedInChannel.accountId) {
      isLoadingConversations.value = false;
      return;
    }

    try {
      isLoadingConversations.value = true;

      const request = {
        channel: "linkedin",
        limit: 50,
        account_id: props.linkedInChannel.accountId,
      };

      const response = await Unipile.getConversations(request);

      chatAccessError.value = null;

      const conversations = Array.isArray(response.data.data)
        ? response.data.data
        : response.data.data?.items || [];

      const transformedConversations = conversations.map((conv) => {
        let contactName = conv.contactName || conv.title || "Unknown";
        if (conv.participants && Array.isArray(conv.participants)) {
          const otherParticipants = conv.participants.filter((p) => !p.is_self);
          if (otherParticipants.length > 0) {
            const participant = otherParticipants[0];
            contactName =
              participant.name ||
              participant.displayName ||
              participant.attendee_name ||
              participant.attendeeName ||
              contactName;
          }
        }
        const isInMail = conv.isInMail === true || conv.content_type === "inmail";
        return {
          ...conv,
          participants: conv.participants || [],
          contactName: contactName,
          title: contactName,
          lastMessage: conv.lastMessage || "",
          lastMessageAt: conv.lastMessageAt || new Date().toISOString(),
          isInMail: isInMail,
        };
      });
      linkedInConversations.value = transformedConversations;

      if (isLinkedInConnected.value && transformedConversations.length > 0) {
        if (!activeChatType.value) {
          activeChatType.value = "classic";
        }
      }

      emit("refresh-conversations");
    } catch (error) {
      console.error("Failed to load LinkedIn conversations:", error);
    } finally {
      isLoadingConversations.value = false;
    }
  }
};

const loadMessagesForConversation = async (conversationId, channelType, loadOlder = false) => {
  if (!conversationId) return;

  try {
    if (loadOlder) {
      isLoadingOlderLinkedInMessages.value = true;
    } else {
      isLoadingMessages.value = true;
      hasMoreLinkedInMessages.value = true;
      oldestLinkedInMessageTimestamp.value = null;
      linkedInMessagesData.value = [];
    }

    const params = {
      limit: 10,
    };

    if (loadOlder && oldestLinkedInMessageTimestamp.value) {
      params.before = oldestLinkedInMessageTimestamp.value;
    }

    const response = await Unipile.getConversationMessages(conversationId, params);
    let messages = response.data.data?.items || response.data.data || [];
    const responseData = response.data.data || response.data;

    const conversation = computedSelectedLinkedInConversation.value;

    const getSenderName = (senderId) => {
      if (!conversation?.participants || !Array.isArray(conversation.participants)) {
        return "Unknown";
      }
      const participant = conversation.participants.find(
        (p) => p.providerUserId === senderId || p.sender_id === senderId || p.id === senderId
      );
      if (participant) {
        return (
          participant.name ||
          participant.displayName ||
          participant.attendee_name ||
          participant.attendeeName ||
          "Unknown"
        );
      }
      return "Unknown";
    };

    messages = messages.map((msg) => {
      let senderName = "Unknown";
      if (msg.is_sender || msg.sender_id === "me" || msg.isOwn) {
        senderName = "You";
      } else if (msg.senderName) {
        senderName = msg.senderName;
      } else if (msg.sender_id) {
        senderName = getSenderName(msg.sender_id);
      }

      const quoted = msg.reply_to
        ? {
            id: msg.reply_to.id,
            text: msg.reply_to.text,
            senderName:
              msg.reply_to.sender_id === msg.sender_id
                ? "You"
                : getSenderName(msg.reply_to.sender_id),
          }
        : msg.quoted || null;

      let readReceipts = msg.readReceipts || [];

      if ((!readReceipts || readReceipts.length === 0) && msg.seen_by) {
        readReceipts = Object.entries(msg.seen_by).map(([id, timestamp]) => ({
          id,
          readAt: timestamp,
          readBy: id,
        }));
      }

      return {
        ...msg,
        senderName,
        quoted,
        readReceipts,
        readCount: readReceipts.length,
      };
    });

    hasMoreLinkedInMessages.value = responseData.has_more !== false;
    if (responseData.oldest_timestamp) {
      oldestLinkedInMessageTimestamp.value = responseData.oldest_timestamp;
    }

    if (loadOlder) {
      linkedInMessagesData.value = [...messages, ...linkedInMessagesData.value];
    } else {
      linkedInMessagesData.value = messages;
      await refreshLinkedInConversationWithParticipants(conversationId);
    }
  } catch (error) {
    console.error(`Failed to load linkedin messages:`, error);
    if (!loadOlder) {
      linkedInMessagesData.value = [];
    }
  } finally {
    if (loadOlder) {
      isLoadingOlderLinkedInMessages.value = false;
    } else {
      isLoadingMessages.value = false;
    }
  }
};

// Starts the first classic (non-InMail) LinkedIn chat with a contact we're already
// connected to but have never messaged — there's no chat id yet, so this goes through
// Unipile's create-chat endpoint (attendees_ids) instead of sendLinkedInMessage, which
// requires an existing conversationId.
const handleStartLinkedInConversation = async (data) => {
  if (isSendingMessage.value || isSendingLinkedInMessage.value) return;

  const channel = props.linkedInChannel;
  if (!channel?.accountId) {
    $toast?.error("LinkedIn not connected");
    return;
  }
  if (!linkedInProviderId.value) {
    $toast?.error("Unable to start conversation: missing LinkedIn profile for this contact");
    return;
  }

  isSendingMessage.value = true;
  try {
    const response = await Unipile.createGroupChat({
      account_id: channel.accountId,
      attendees_ids: [linkedInProviderId.value],
      text: data.text,
      contactId: props.selectedBucket?.contact?.id,
    });

    if (response?.data?.status) {
      await loadConversations();
      emit("message-sent");
    } else {
      throw new Error("Failed to send message");
    }
  } catch (error) {
    console.error("Failed to start LinkedIn conversation:", error);
    $toast?.error(error.response?.data?.message || "Failed to send message");
  } finally {
    isSendingMessage.value = false;
  }
};

const handleSendMessage = async (data) => {
  await showUsageBasedFeaturesModalOnce("linkedin_message", async () => {
    const conversation = computedSelectedLinkedInConversation.value;

    if (!conversation) {
      if (activeChatType.value !== "inmail" && linkedInProviderId.value) {
        await handleStartLinkedInConversation(data);
      } else {
        $toast?.error("No conversation selected");
      }
      return;
    }

    if (isSendingMessage.value || isSendingLinkedInMessage.value) {
      return;
    }

    const channel = props.linkedInChannel;
    if (!channel?.accountId) {
      $toast?.error("LinkedIn not connected");
      return;
    }

    let optimisticMessage = null;
    const optimisticMessageId = `temp-${Date.now()}`;

    try {
      const requestPayload = {};

      if (data.text) {
        requestPayload.text = data.text;
      }

      if (replyingTo.value?.id) {
        requestPayload.quote_id = replyingTo.value.id;
      }
      if (data.attachments && Array.isArray(data.attachments) && data.attachments.length > 0) {
        requestPayload.attachments = data.attachments;
      }
      if (data.voice_message) {
        requestPayload.voice_message = data.voice_message;
      }
      if (data.video_message) {
        requestPayload.video_message = data.video_message;
      }

      let optimisticText = requestPayload.text || "";
      if (optimisticText) {
        optimisticText = formatOptimisticLinkMessage(optimisticText);
      }

      optimisticMessage = {
        id: optimisticMessageId,
        text: optimisticText,
        senderName: "You",
        is_sender: true,
        isOwn: true,
        isPending: true,
        timestamp: new Date().toISOString(),
        attachments: [],
      };

      if (replyingTo.value) {
        optimisticMessage.quoted = {
          id: replyingTo.value.id || replyingTo.value.unipileMessageId,
          text: replyingTo.value.text || "",
          sender_id: replyingTo.value.sender_id || replyingTo.value.senderId,
          senderName: replyingTo.value.senderName || (replyingTo.value.isOwn ? "You" : "Unknown"),
          attachments: replyingTo.value.attachments || [],
        };
      }

      linkedInMessagesData.value = [...(linkedInMessagesData.value || []), optimisticMessage];
      replyingTo.value = null;
      isSendingMessage.value = true;

      try {
        const response = await sendLinkedInMessage({
          conversationId: conversation.id,
          ...requestPayload,
        });

        if (response?.data?.message_id) {
          const messageId = response.data.message_id;
          linkedInMessagesData.value = (linkedInMessagesData.value || []).map((msg) => {
            if (msg.id === optimisticMessageId) {
              return {
                ...msg,
                unipileMessageId: messageId,
                isPending: false,
                _stableKey: msg.id,
              };
            }
            return msg;
          });
        }

        isSendingMessage.value = false;
        emit("message-sent");
      } catch (error) {
        console.error("Failed to send message:", error);
        linkedInMessagesData.value = (linkedInMessagesData.value || []).filter(
          (msg) => msg.id !== optimisticMessageId
        );
        if (!error?.absorbedButRecoverable) {
          $toast?.error(error.response?.data?.message || "Failed to send message");
        }
        replyingTo.value = null;
        isSendingMessage.value = false;
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      if (optimisticMessage) {
        linkedInMessagesData.value = (linkedInMessagesData.value || []).filter(
          (msg) => msg.id !== optimisticMessageId
        );
      }
      if (!error?.absorbedButRecoverable) {
        $toast?.error(error.response?.data?.message || "Failed to send message");
      }
      isSendingMessage.value = false;
    }
  });
};

const loadLinkedInProfile = async () => {
  refetchProfile();
};

const handleConnectionRequest = () => {
  linkedInConnectionInitialMessage.value = "";
  linkedInInviteError.value = null;
  showLinkedInConnectionModal.value = true;
};

const handleLinkedInConnectionModalClose = () => {
  showLinkedInConnectionModal.value = false;
  linkedInInviteError.value = null;
};

const handleLinkedInConnectionSend = async (data) => {
  try {
    linkedInInviteError.value = null;

    const linkedInUrl = props.selectedBucket?.contact?.linkedInUrl;
    if (!linkedInUrl) {
      linkedInInviteError.value = "Add LinkedIn URL to contact";
      return;
    }

    if (!props.linkedInChannel?.accountId) {
      linkedInInviteError.value = "LinkedIn not connected";
      return;
    }

    if (!isChannelHealthyStatus(props.linkedInChannel?.status)) {
      linkedInInviteError.value = "LinkedIn disconnected. Reconnect required";
      return;
    }

    const identifier = extractLinkedInIdentifierFromUrl(linkedInUrl);
    if (!identifier) {
      linkedInInviteError.value = "Invalid LinkedIn URL";
      return;
    }

    await sendLinkedInConnectionRequest({
      identifier,
      accountId: props.linkedInChannel.accountId,
      message: data.message || "",
      contactId: props.selectedBucket?.contact?.id,
    });

    $toast?.success("Connection request sent");
    showLinkedInConnectionModal.value = false;
    invitationStatus.value = { type: "SENT", status: "PENDING" };
    await loadConversations();
    refetchConnectionStatus();
  } catch (error) {
    console.error("Failed to send LinkedIn connection request:", error);
    linkedInInviteError.value =
      error.response?.data?.message ||
      error.response?.data?.errorMessage ||
      "Failed to send connection request";
  }
};

function setupMessageWebhooks() {
  if (messageWebhooksCleanup.value) {
    messageWebhooksCleanup.value();
  }

  const { setupSocket, cleanupSocket } = useMessageWebhooks({
    onLinkedInMessage: (payload, helpers) => {
      try {
        const isInMailChat =
          payload.chat?.content_type === "inmail" ||
          payload.chat?.meta?.isInMail === true ||
          payload.meta?.isInMail === true;

        if (isInMailChat && isLinkedInConnected.value && activeChatType.value !== "inmail") {
          activeChatType.value = "inmail";
          loadConversations();
        }

        if (!computedSelectedLinkedInConversation.value) {
          refetchChatByContact();
          return;
        }

        if (!matchesConversation(payload, computedSelectedLinkedInConversation.value)) {
          return;
        }

        const newMessage = helpers.createMessage(payload);
        const messageId = newMessage.id || newMessage.unipileMessageId;

        const optimisticIndex = linkedInMessagesData.value.findIndex(
          (msg) =>
            msg.unipileMessageId === messageId || (msg.id?.startsWith("temp-") && msg.isPending)
        );

        if (optimisticIndex !== -1) {
          const existing = linkedInMessagesData.value[optimisticIndex];
          // Skip if already confirmed by mutation response
          if (existing.unipileMessageId === messageId && !existing.isPending) {
            refetchChatByContact();
            return;
          }
          linkedInMessagesData.value.splice(optimisticIndex, 1, {
            ...newMessage,
            _stableKey: existing.id,
          });
        } else if (!helpers.checkMessageExists(newMessage, linkedInMessagesData.value)) {
          linkedInMessagesData.value.push(newMessage);
          linkedInMessagesData.value.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.createdAt || 0).getTime();
            const timeB = new Date(b.timestamp || b.createdAt || 0).getTime();
            return timeA - timeB;
          });
        }

        // Refresh conversation list so lastMessage preview updates
        refetchChatByContact();
      } catch (error) {
        console.error("Error handling LinkedIn message received socket:", error);
      }
    },
    onMessageReaction: (payload) => {
      try {
        const idx = linkedInMessagesData.value.findIndex(
          (msg) => msg.unipileMessageId === payload.unipileMessageId || msg.id === payload.messageId
        );
        if (idx === -1) return;

        linkedInMessagesData.value.splice(idx, 1, {
          ...linkedInMessagesData.value[idx],
          reactions: payload.reactions,
        });
      } catch (error) {
        console.error("Error handling LinkedIn message reaction socket:", error);
      }
    },
  });

  setupSocket();
  messageWebhooksCleanup.value = cleanupSocket;
}

const handleInmailSent = async () => {
  showInmailModal.value = false;
  await loadConversations();

  if (pendingInMails.value.length > 0) {
    nextTick(() => {
      const container = inmailMessagesContainer.value;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  if (props.linkedInChannel?.accountId) {
    await refetchInmailBalance();
  }

  $toast?.success("InMail sent");
};

const handleChatTypeChange = async (type) => {
  activeChatType.value = type;
  const filtered = filteredLinkedInConversations.value;
  if (filtered.length > 0) {
    const newConversation = filtered[0];
    const isSameConversation = selectedLinkedInConversationId.value === newConversation.id;

    if (!isSameConversation) {
      selectedLinkedInConversationId.value = newConversation.id;
      selectedLinkedInConversation.value = newConversation;

      const oldConversationId = selectedLinkedInConversation.value?.id;
      const messagesForDifferentConv =
        linkedInMessagesData.value.length > 0 &&
        oldConversationId &&
        oldConversationId !== newConversation.id;

      if (messagesForDifferentConv || linkedInMessagesData.value.length === 0) {
        await loadMessagesForConversation(newConversation.id, "linkedin");
      }
    }
  } else {
    selectedLinkedInConversationId.value = null;
    selectedLinkedInConversation.value = null;
    linkedInMessagesData.value = [];

    if (type === "inmail" && props.linkedInChannel?.accountId) {
      if (inmailBalance.value === null && !isLoadingInmailBalance.value) {
        await refetchInmailBalance();
      }
    }
  }
};

watch(
  () => props.linkedInChannel,
  (newChannel, oldChannel) => {
    if (!oldChannel && newChannel) return;
    if (newChannel && newChannel.accountId !== oldChannel?.accountId) {
      setupMessageWebhooks();
      loadConversations();
      if (props.selectedBucket?.contact?.linkedInUrl) {
        refetchConnectionStatus();
      }
    }
  }
);

watch(
  () => props.selectedBucket,
  (newBucket, oldBucket) => {
    if (!oldBucket && newBucket) return;
    if (newBucket?.contact?.id !== oldBucket?.contact?.id && props.linkedInChannel) {
      loadConversations();
      if (props.selectedBucket?.contact?.linkedInUrl) {
        refetchConnectionStatus();
      }
    }
  }
);

watch(activeLinkedInTab, (newTab) => {
  if (newTab === "profile") {
    refetchProfile();
  } else if (newTab === "activities") {
    linkedInActivitiesLoaded.value = true;
  } else if (newTab === "messages") {
    if (!chatByContactData.value) {
      loadConversations();
    }
    if (
      !connectionStatusData.value &&
      props.selectedBucket?.contact?.linkedInUrl &&
      props.linkedInChannel
    ) {
      refetchConnectionStatus();
    }
  }
});
onMounted(() => {
  if (props.linkedInChannel) {
    setupMessageWebhooks();
    loadConversations();
    if (props.selectedBucket?.contact?.linkedInUrl) {
      refetchConnectionStatus();
    }
  }
});

onBeforeUnmount(() => {
  if (messageWebhooksCleanup.value) {
    messageWebhooksCleanup.value();
  }
});

watch(
  () => props.pendingCueCardContent,
  async (payload) => {
    if (
      payload &&
      (payload.channel === "linkedin_connection" ||
        payload.channel === "linkedin_followup" ||
        payload.channel === "linkedin_inmail") &&
      payload.content
    ) {
      await handleUseCueCardContent(payload);
      emit("content-applied");
    }
  }
);
</script>

<style scoped>
.call-panel-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
}

.linkedin-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.linkedin-subtabs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  position: relative;
  z-index: 10;
  flex-shrink: 0;
}

.subtab {
  border: none;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  min-height: 24px;
  flex-shrink: 0;
}

.subtab.active {
  background: #3e3efe;
  color: #fff;
}

.subtab.ghost {
  background: #f1f2ff;
  color: #5f5fcf;
}

.linkedin-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.linkedin-messages-layout {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.linkedin-messages-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  gap: 0;
}

.linkedin-message-view-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.linkedin-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #7b7fa8;
  font-size: 0.875rem;
  flex: 1;
  min-height: 0;
}

.linkedin-empty-state--missing-url {
  padding: 80px 20px;
}

.linkedin-empty-state__content {
  text-align: center;
}

.linkedin-empty-state__icon {
  font-size: 48px;
  color: #0077b5;
  margin-bottom: 16px;
}

.linkedin-empty-state__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.linkedin-empty-state__message {
  font-size: 0.875rem;
  color: #7b7fa8;
  margin-bottom: 16px;
}

.connection-status-loading {
  margin-left: auto;
  display: flex;
  align-items: center;
  min-height: 28px;
  flex-shrink: 0;
}

.pill-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  min-height: 28px;
  box-sizing: border-box;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.pill-btn.primary {
  background: #3e3efe;
  color: #fff;
}

.pill-btn.primary:hover:not(:disabled) {
  background: #2d2dff;
}

.pill-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.linkedin-messages-layout {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.linkedin-messages-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.linkedin-message-view-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.linkedin-message-view-wrapper :deep(.linkedin-message-view) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.linkedin-message-view-wrapper :deep(.message-composer) {
  flex: 0 0 auto;
}

.linkedin-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #7b7fa8;
  font-size: 0.875rem;
  gap: 12px;
  flex: 1;
  min-height: 0;
}

.linkedin-empty-state--missing-url {
  margin: 0;
  padding: 80px 40px;
}

.linkedin-empty-state__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 400px;
  gap: 16px;
}

.linkedin-empty-state__icon {
  font-size: 3.5rem;
  color: #0077b5;
  margin-bottom: 8px;
}

.linkedin-empty-state__title {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  line-height: 1.4;
}

.linkedin-empty-state__message {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.linkedin-disconnected-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  min-height: 200px;
  width: 100%;
}

.linkedin-disconnected-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 240px;
}

.linkedin-disconnected-icon {
  font-size: 2rem;
  color: #d1d5db;
  margin-bottom: 0.75rem;
}

.linkedin-disconnected-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.375rem 0;
}

.linkedin-disconnected-message {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.linkedin-disconnected-action {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: #3e3efe;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.linkedin-disconnected-action:hover {
  background: #3230d6;
}

.linkedin-disconnected-action .iconify {
  font-size: 0.875rem;
}

.linkedin-loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  min-height: 200px;
  width: 100%;
}

.linkedin-inmail-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
  min-height: 300px;
}

.linkedin-inmail-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 320px;
}

.linkedin-inmail-icon {
  font-size: 2.5rem;
  color: #0077b5;
  margin-bottom: 0.75rem;
}

.linkedin-inmail-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.375rem 0;
}

.linkedin-inmail-message {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1.25rem 0;
}

.linkedin-inmail-credits {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: linear-gradient(135deg, rgba(62, 62, 254, 0.1) 0%, rgba(50, 48, 214, 0.1) 100%);
  border: 1px solid rgba(62, 62, 254, 0.2);
  border-radius: 8px;
  margin-bottom: 1.25rem;
}

.credits-label {
  font-size: 0.6875rem;
  color: #6b7280;
  font-weight: 500;
}

.credits-value {
  font-size: 0.8125rem;
  color: #3e3efe;
  font-weight: 600;
}

.linkedin-inmail-chip {
  display: inline-block;
  padding: 4px 8px;
  background: rgba(249, 115, 22, 0.1);
  border: 1px solid rgba(249, 115, 22, 0.3);
  border-radius: 4px;
  font-size: 0.6875rem;
  color: #ea580c;
  font-weight: 500;
  margin-bottom: 8px;
  line-height: 1;
}

.connection-status-loading {
  margin-left: auto;
  display: flex;
  align-items: center;
  min-height: 28px;
  flex-shrink: 0;
}

.connection-status-connected {
  margin-left: auto;
  padding: 4px 10px;
  min-height: 24px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  font-size: 13px;
  color: var(--success-color, #10b981);
  font-weight: 500;
  transition: all 0.2s ease;
}

.connection-status-pending {
  margin-left: auto;
  padding: 4px 10px;
  min-height: 24px;
  box-sizing: border-box;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 12px;
  font-size: 11px;
  color: #f59e0b;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 3px;
}

.connection-status-pending__icon {
  font-size: 12px;
}

.linkedin-inmail-thread {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: transparent;
  overflow: hidden;
}

.linkedin-inmail-thread__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 12px;
}

.linkedin-inmail-thread__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.linkedin-inmail-thread__icon {
  font-size: 1.5rem;
}

.linkedin-inmail-thread__header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.linkedin-inmail-thread__title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.01em;
}

.linkedin-inmail-thread__status {
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.linkedin-inmail-thread__status::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.linkedin-inmail-thread__status--connected {
  color: #10b981;
}

.linkedin-inmail-thread__status--connected::before {
  background: #10b981;
}

.linkedin-inmail-thread__status--disconnected {
  color: #f59e0b;
}

.linkedin-inmail-thread__status--disconnected::before {
  background: #f59e0b;
}

.linkedin-inmail-thread__credits-skeleton {
  width: 72px;
  height: 28px;
  border-radius: 20px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.linkedin-inmail-thread__credits {
  display: flex;
  transition: background 0.2s ease;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: rgba(62, 62, 254, 0.06);
  border-radius: 20px;
}

.linkedin-inmail-thread__credits .credits-value {
  font-size: 0.8125rem;
  color: #3e3efe;
  font-weight: 600;
}

.linkedin-inmail-thread__credits .credits-label {
  font-size: 0.6875rem;
  color: #7b7fa8;
}

.linkedin-inmail-thread__credits--empty {
  background: rgba(239, 68, 68, 0.06);
}

.linkedin-inmail-thread__credits--empty .credits-value {
  color: #ef4444;
}

.linkedin-inmail-thread__credits--empty .credits-label {
  color: #b91c1c;
}

.linkedin-inmail-thread__messages {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: transparent;
  scroll-behavior: smooth;
}

.linkedin-inmail-thread__messages::-webkit-scrollbar {
  width: 6px;
}

.linkedin-inmail-thread__messages::-webkit-scrollbar-track {
  background: transparent;
}

.linkedin-inmail-thread__messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.linkedin-inmail-thread__messages::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.linkedin-inmail-thread__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  flex: 1;
}

.linkedin-inmail-thread__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  flex: 1;
}

.linkedin-inmail-thread__empty-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #eef0ff 0%, #f5f3ff 100%);
  margin-bottom: 16px;
}

.linkedin-inmail-thread__empty-icon-wrap--warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fff7ed 100%);
}

.linkedin-inmail-thread__empty-icon {
  font-size: 1.375rem;
  color: #6366f1;
}

.linkedin-inmail-thread__empty-icon-wrap--warning .linkedin-inmail-thread__empty-icon {
  color: #f59e0b;
}

.linkedin-inmail-thread__empty-heading {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 6px 0;
  letter-spacing: -0.01em;
}

.linkedin-inmail-thread__empty-text {
  font-size: 0.8125rem;
  color: #9ca3af;
  line-height: 1.7;
  margin: 0;
  max-width: 280px;
}

.linkedin-inmail-thread__message {
  max-width: 85%;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f3f4f6;
  border: none;
  transition: background 0.15s ease;
}

.linkedin-inmail-thread__message:hover {
  background: #e5e7eb;
}

.linkedin-inmail-thread__message--sent {
  align-self: flex-end;
  background: #3e3efe;
  color: #ffffff;
}

.linkedin-inmail-thread__message--sent:hover {
  background: #3230d6;
}

.linkedin-inmail-thread__message-content {
  margin-bottom: 0;
}

.linkedin-inmail-thread__message-subject {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 6px;
  letter-spacing: -0.01em;
}

.linkedin-inmail-thread__message--sent .linkedin-inmail-thread__message-subject {
  color: #ffffff;
}

.linkedin-inmail-thread__message-text {
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  letter-spacing: 0.01em;
}

.linkedin-inmail-thread__message--sent .linkedin-inmail-thread__message-text {
  color: rgba(255, 255, 255, 0.9);
}

.linkedin-inmail-thread__message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}

.linkedin-inmail-thread__message-time {
  font-size: 0.75rem;
  opacity: 0.85;
  font-weight: 400;
}

.linkedin-inmail-thread__message-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  opacity: 0.9;
  font-weight: 500;
}

.linkedin-inmail-thread__message-status .iconify {
  font-size: 0.875rem;
}

.linkedin-inmail-thread__waiting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 14px;
  background: rgba(245, 158, 11, 0.06);
  border-radius: 10px;
  font-size: 0.8125rem;
  color: #b45309;
  font-weight: 500;
  margin-top: 4px;
}

.linkedin-inmail-thread__waiting .iconify {
  font-size: 0.875rem;
}

.linkedin-inmail-thread__compose {
  padding: 12px 20px 16px;
  background: transparent;
  display: flex;
  justify-content: center;
}

.linkedin-inmail-thread__send-btn {
  width: 100%;
  font-weight: 600;
  transition: all 0.2s ease;
  border-radius: 10px;
}

.linkedin-inmail-thread__send-btn--disabled {
  opacity: 0.5;
}

.linkedin-inmail-thread__send-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.linkedin-connection-request-sent {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.linkedin-connection-request-sent__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  border-bottom: 1px solid #e5e7eb;
}

.linkedin-connection-request-sent__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.linkedin-connection-request-sent__icon {
  font-size: 1.5rem;
  color: #0077b5;
  filter: drop-shadow(0 1px 2px rgba(0, 119, 181, 0.2));
}

.linkedin-connection-request-sent__header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.linkedin-connection-request-sent__title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.01em;
}

.linkedin-connection-request-sent__status {
  font-size: 0.75rem;
  color: #f59e0b;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.linkedin-connection-request-sent__messages {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fafbff;
  scroll-behavior: smooth;
}

.linkedin-connection-request-sent__messages::-webkit-scrollbar {
  width: 6px;
}

.linkedin-connection-request-sent__messages::-webkit-scrollbar-track {
  background: transparent;
}

.linkedin-connection-request-sent__messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.linkedin-connection-request-sent__messages::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.linkedin-connection-request-sent__message {
  max-width: 85%;
  padding: 14px 16px;
  border-radius: 14px;
  align-self: flex-end;
  background: linear-gradient(135deg, #0077b5 0%, #005885 100%);
  border: none;
  color: #ffffff;
  box-shadow:
    0 2px 4px rgba(0, 119, 181, 0.2),
    0 1px 2px rgba(0, 119, 181, 0.15);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.linkedin-connection-request-sent__message:hover {
  transform: translateY(-1px);
  box-shadow:
    0 4px 6px rgba(0, 119, 181, 0.25),
    0 2px 4px rgba(0, 119, 181, 0.2);
}

.linkedin-connection-request-sent__message-content {
  margin-bottom: 0;
}

.linkedin-connection-request-sent__message-text {
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  color: rgba(255, 255, 255, 0.98);
  letter-spacing: 0.01em;
}

.linkedin-connection-request-sent__message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

.linkedin-connection-request-sent__message-time {
  font-size: 0.75rem;
  opacity: 0.85;
  font-weight: 400;
}

.linkedin-connection-request-sent__message-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  opacity: 0.9;
  font-weight: 500;
}

.linkedin-connection-request-sent__message-status .iconify {
  font-size: 0.875rem;
}

.linkedin-connection-request-sent__waiting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(0, 119, 181, 0.08) 0%, rgba(0, 88, 133, 0.08) 100%);
  border: 1.5px dashed rgba(0, 119, 181, 0.3);
  border-radius: 10px;
  font-size: 0.8125rem;
  color: #0077b5;
  font-weight: 500;
  margin-top: 4px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.linkedin-connection-request-sent__waiting .iconify {
  font-size: 0.875rem;
}

.linkedin-chat-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
  padding: 0 4px 0 0;
  border-bottom: 2px solid #e5e7eb;
  background: #ffffff;
}

.chat-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: transparent;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  margin-bottom: -2px;
  position: relative;
  border-radius: 6px 6px 0 0;
}

.chat-tab:hover {
  color: #3e3efe;
  background: rgba(62, 62, 254, 0.05);
}

.chat-tab.active {
  color: #3e3efe;
  border-bottom-color: #3e3efe;
  font-weight: 600;
  background: rgba(62, 62, 254, 0.05);
}

.chat-tab.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: #3e3efe;
  border-radius: 2px 2px 0 0;
}

/* Chat Access Error UI */
.linkedin-chat-access-error {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  min-height: 400px;
  width: 100%;
}

.linkedin-chat-access-error__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 500px;
  width: 100%;
}

.linkedin-chat-access-error__icon {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.linkedin-chat-access-error__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.75rem 0;
}

.linkedin-chat-access-error__message {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
}

.linkedin-chat-access-error__pending {
  width: 100%;
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  text-align: left;
}

.linkedin-chat-access-error__pending-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.75rem;
}

.linkedin-chat-access-error__pending-header .iconify {
  font-size: 1.125rem;
  color: #3e3efe;
}

.linkedin-chat-access-error__pending-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.linkedin-chat-access-error__pending-item {
  padding: 0.75rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.linkedin-chat-access-error__pending-subject {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.25rem;
}

.linkedin-chat-access-error__pending-date {
  font-size: 0.75rem;
  color: #6b7280;
}

.linkedin-chat-access-error__actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.linkedin-chat-access-error__actions .ui-button {
  min-width: 160px;
}

/* Was-connected notice banner (LinkedIn-style) */
.linkedin-was-connected-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.linkedin-was-connected-notice__icon {
  font-size: 1.1rem;
  color: #6b7280;
  flex-shrink: 0;
}

.linkedin-was-connected-notice__text {
  font-size: 0.8rem;
  color: #374151;
  line-height: 1.4;
}

.linkedin-was-connected-notice__text strong {
  color: #111827;
}

.linkedin-not-connected-state,
.linkedin-restricted-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  text-align: center;
  gap: 0.5rem;
  flex: 1;
}

.linkedin-empty-state-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.linkedin-empty-state-icon :deep(svg) {
  width: 24px;
  height: 24px;
  color: #9ca3af;
}

.linkedin-empty-state-heading {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.linkedin-empty-state-text {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
  max-width: 220px;
}

.linkedin-chat-error-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.chat-tab__icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
</style>
